/**
 * EmergencyOverlay — CTRL+ALT+DEL-style task manager for ECAD Recorder
 *
 * Activated by double-ESC. Shows:
 * - Current phase and performance mode
 * - All tracked resources (RAF, intervals, listeners, audio)
 * - Watchdog stats per handler
 * - Transition history
 * - Buttons: Resume | Kill All | Change Perf Mode
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, XCircle, PlayCircle, Zap, Activity, Trash2 } from 'lucide-react';
import type { RecorderHealthAPI, PerformanceMode } from '../hooks/useRecorderHealth';

interface EmergencyOverlayProps {
  health: RecorderHealthAPI;
}

export function EmergencyOverlay({ health }: EmergencyOverlayProps) {
  const [report, setReport] = useState(() => health.getReport());

  // Refresh report every 500ms while overlay is open
  useEffect(() => {
    const interval = setInterval(() => {
      setReport(health.getReport());
    }, 500);
    return () => clearInterval(interval);
  }, [health]);

  // Single ESC to exit emergency mode when overlay is visible
  useEffect(() => {
    if (!health.isEmergency) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        health.exitEmergency();
      }
    };

    // Small delay to avoid the double-ESC that activated emergency from also closing it
    const timer = setTimeout(() => {
      window.addEventListener('keydown', handleEsc, { capture: true });
    }, 600);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', handleEsc, { capture: true } as EventListenerOptions);
    };
  }, [health.isEmergency, health]);

  if (!health.isEmergency) return null;

  const fmtAge = (ms: number) => ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

  const handleResume = () => {
    health.exitEmergency();
  };

  const handleKillAll = () => {
    health.cleanupAll();
    setReport(health.getReport());
  };

  const handleSetMode = (mode: PerformanceMode) => {
    health.setPerfMode(mode);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 999999,
      background: 'rgba(0, 0, 0, 0.92)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
      animation: 'emergencyFadeIn 0.2s ease-out',
    }}>
      <style>{`
        @keyframes emergencyFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes emergencyPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(244, 67, 54, 0.3); }
          50% { box-shadow: 0 0 40px rgba(244, 67, 54, 0.6); }
        }
        .emergency-panel {
          background: #1a1a2e;
          border: 2px solid #f44336;
          border-radius: 16px;
          width: 680px;
          max-height: 85vh;
          overflow-y: auto;
          padding: 0;
          animation: emergencyPulse 2s ease-in-out infinite;
        }
        .emergency-panel::-webkit-scrollbar { width: 6px; }
        .emergency-panel::-webkit-scrollbar-track { background: transparent; }
        .emergency-panel::-webkit-scrollbar-thumb { background: #f44336; border-radius: 3px; }
        .em-header {
          background: linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%);
          padding: 20px 24px;
          display: flex;
          align-items: center;
          gap: 14px;
          border-radius: 14px 14px 0 0;
        }
        .em-header h2 { margin: 0; color: #fff; font-size: 18px; font-weight: 700; }
        .em-header .em-sub { color: rgba(255,255,255,0.7); font-size: 12px; margin-top: 2px; }
        .em-section { padding: 16px 24px; border-bottom: 1px solid rgba(255,255,255,0.06); }
        .em-section-title { color: #90caf9; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .em-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; color: #e0e0e0; font-size: 13px; }
        .em-label { color: #9e9e9e; }
        .em-value { font-family: 'Cascadia Code', 'Fira Code', monospace; color: #fff; font-size: 12px; }
        .em-value.hot { color: #ff9800; }
        .em-value.danger { color: #f44336; }
        .em-value.ok { color: #4caf50; }
        .em-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
        .em-badge.recording { background: rgba(244, 67, 54, 0.2); color: #f44336; }
        .em-badge.editing { background: rgba(33, 150, 243, 0.2); color: #2196f3; }
        .em-badge.idle { background: rgba(158, 158, 158, 0.2); color: #9e9e9e; }
        .em-badge.emergency { background: rgba(244, 67, 54, 0.3); color: #ff5252; }
        .em-actions { padding: 20px 24px; display: flex; gap: 10px; justify-content: center; }
        .em-btn {
          padding: 10px 20px; border-radius: 8px; border: none; cursor: pointer;
          font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 6px;
          transition: all 0.15s ease;
        }
        .em-btn:hover { transform: translateY(-1px); }
        .em-btn.primary { background: #4caf50; color: #fff; }
        .em-btn.primary:hover { background: #66bb6a; }
        .em-btn.danger { background: #f44336; color: #fff; }
        .em-btn.danger:hover { background: #ef5350; }
        .em-btn.mode { background: rgba(255,255,255,0.08); color: #fff; border: 1px solid rgba(255,255,255,0.15); }
        .em-btn.mode:hover { background: rgba(255,255,255,0.15); }
        .em-btn.mode.active { border-color: #2196f3; background: rgba(33, 150, 243, 0.15); }
        .em-resource-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .em-resource-card { background: rgba(255,255,255,0.04); border-radius: 8px; padding: 10px 14px; }
        .em-resource-card .count { font-size: 24px; font-weight: 700; color: #fff; }
        .em-resource-card .label { font-size: 11px; color: #9e9e9e; text-transform: uppercase; }
        .em-watchdog-item { background: rgba(255,255,255,0.03); border-radius: 6px; padding: 8px 12px; margin-bottom: 4px; display: flex; justify-content: space-between; align-items: center; }
        .em-watchdog-name { font-family: monospace; font-size: 12px; color: #e0e0e0; }
        .em-watchdog-stats { display: flex; gap: 12px; font-size: 11px; color: #9e9e9e; }
        .em-transition-item { font-family: monospace; font-size: 11px; color: #757575; padding: 3px 0; }
      `}</style>

      <div className="emergency-panel">
        {/* Header */}
        <div className="em-header">
          <AlertTriangle size={28} color="#fff" />
          <div>
            <h2>ECAD Emergency Stop</h2>
            <div className="em-sub">
              Todas as operações foram suspensas. Double-ESC ativou o modo de emergência.
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="em-section">
          <div className="em-section-title"><Activity size={14} /> Status</div>
          <div className="em-row">
            <span className="em-label">Fase anterior</span>
            <span className={`em-badge ${report.phase.toLowerCase()}`}>{report.phase}</span>
          </div>
          <div className="em-row">
            <span className="em-label">Modo de performance</span>
            <span className={`em-value ${report.perfMode === 'LIGHT' ? 'danger' : report.perfMode === 'MODERATE' ? 'hot' : 'ok'}`}>
              {report.perfMode === 'LIGHT' ? '🟡' : report.perfMode === 'MODERATE' ? '🟠' : '🟢'} {report.perfMode}
            </span>
          </div>
          <div className="em-row">
            <span className="em-label">Uptime</span>
            <span className="em-value">{fmtAge(report.uptime)}</span>
          </div>
        </div>

        {/* Resources */}
        <div className="em-section">
          <div className="em-section-title"><Zap size={14} /> Recursos Ativos ({report.resources.total})</div>
          <div className="em-resource-grid">
            <div className="em-resource-card">
              <div className="count" style={{ color: report.resources.rafs > 0 ? '#ff9800' : '#4caf50' }}>{report.resources.rafs}</div>
              <div className="label">RAF Loops</div>
            </div>
            <div className="em-resource-card">
              <div className="count" style={{ color: report.resources.intervals > 0 ? '#ff9800' : '#4caf50' }}>{report.resources.intervals}</div>
              <div className="label">Intervals</div>
            </div>
            <div className="em-resource-card">
              <div className="count" style={{ color: report.resources.listeners > 2 ? '#ff9800' : '#4caf50' }}>{report.resources.listeners}</div>
              <div className="label">Listeners</div>
            </div>
            <div className="em-resource-card">
              <div className="count" style={{ color: report.resources.audios > 0 ? '#ff9800' : '#4caf50' }}>{report.resources.audios}</div>
              <div className="label">Audio</div>
            </div>
          </div>
        </div>

        {/* Watchdog */}
        {Object.keys(report.watchdog).length > 0 && (
          <div className="em-section">
            <div className="em-section-title"><AlertTriangle size={14} /> Watchdog — Frequência de Handlers</div>
            {Object.entries(report.watchdog).map(([name, w]) => (
              <div key={name} className="em-watchdog-item">
                <span className="em-watchdog-name">
                  {w.warnings > 0 ? '⚠️' : '✅'} {name}
                </span>
                <span className="em-watchdog-stats">
                  <span>calls: {w.calls}</span>
                  <span>avg: {w.avg}</span>
                  <span style={{ color: w.blocked > 0 ? '#f44336' : '#4caf50' }}>blocked: {w.blocked}</span>
                  <span style={{ color: w.warnings > 0 ? '#ff9800' : '#4caf50' }}>warns: {w.warnings}</span>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Transitions */}
        {report.transitions.length > 0 && (
          <div className="em-section">
            <div className="em-section-title">Histórico de Transições (últimas {report.transitions.length})</div>
            {report.transitions.slice(-6).reverse().map((t, i) => (
              <div key={i} className="em-transition-item">
                {t.from} → {t.to} ({fmtAge(Date.now() - t.timestamp)} atrás, limpou {t.cleanedResources} recursos)
              </div>
            ))}
          </div>
        )}

        {/* Performance Mode Selector */}
        <div className="em-section">
          <div className="em-section-title">Modo de Performance (ao retomar)</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['HIGH', 'MODERATE', 'LIGHT'] as PerformanceMode[]).map(mode => (
              <button
                key={mode}
                className={`em-btn mode ${health.perfMode === mode ? 'active' : ''}`}
                onClick={() => handleSetMode(mode)}
              >
                {mode === 'HIGH' ? '🟢' : mode === 'MODERATE' ? '🟠' : '🟡'} {mode}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#757575', marginTop: 8 }}>
            {health.perfMode === 'HIGH' && 'Velocidade máxima — sem throttle nos handlers'}
            {health.perfMode === 'MODERATE' && 'Intervals mínimos 2x mais lentos — reduz carga'}
            {health.perfMode === 'LIGHT' && 'Intervals mínimos 4x mais lentos — modo seguro'}
          </div>
        </div>

        {/* Actions */}
        <div className="em-actions">
          <button className="em-btn primary" onClick={handleResume}>
            <PlayCircle size={16} /> Retomar Operações
          </button>
          <button className="em-btn danger" onClick={handleKillAll}>
            <Trash2 size={16} /> Kill All Resources
          </button>
        </div>

        {/* Footer hint */}
        <div style={{ padding: '12px 24px', textAlign: 'center', fontSize: 11, color: '#616161', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          Pressione ESC para fechar ou use <code style={{ color: '#90caf9' }}>_ecadHealth.dump()</code> no console para diagnóstico completo
        </div>
      </div>
    </div>
  );
}
